/**
 * Renders a multi-select checkbox-style option list with a submit button.
 *
 * Each option is toggleable (add/remove from selection set).
 * Submit button is disabled until `minSelect` is met.
 * After answering, selected items are highlighted and the submit button is hidden.
 *
 * Uses Deckard's theme palette tokens for proper light/dark mode support.
 */
import { Button } from '@components/button';
import { Checkbox } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Box, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import type { RichBlockProps } from './richBlockTypes';

export const MultiChoiceBlock = ({ block, onRespond }: RichBlockProps) => {
  const { palette } = useTheme();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Only block interaction when explicitly answered — expired blocks remain
  // clickable so the user can re-send their selection as a new message.
  const isAnswered = block.status === 'answered';

  // Extract answered selections from persisted response for history reconstruction
  const answeredSelections = (block.response as { selections?: string[] } | undefined)?.selections;

  const {
    title,
    description,
    options = [],
    minSelect = 1,
    maxSelect,
  } = block.payload as {
    title?: string;
    description?: string;
    options: Array<{ label: string; description?: string }>;
    minSelect?: number;
    maxSelect?: number;
  };

  const handleToggle = (label: string) => {
    if (isAnswered) return;

    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        // Enforce maxSelect: if at the limit, don't add more
        if (maxSelect && next.size >= maxSelect) return prev;
        next.add(label);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (isAnswered || selected.size < minSelect) return;
    onRespond?.(block.blockId, { selections: Array.from(selected) });
  };

  /** Check if an option is selected — uses live state or persisted response */
  const isOptionSelected = (label: string): boolean => {
    if (isAnswered && answeredSelections) {
      return answeredSelections.includes(label);
    }
    return selected.has(label);
  };

  const canSubmit = selected.size >= minSelect && (!maxSelect || selected.size <= maxSelect);

  return (
    <Stack mt={1} mb={2} gap={1} data-testid="rich-block-multi-choice">
      {title && (
        <Typography variant="subtitle1" color="agentChat.message.agent.foreground.default">
          {title}
        </Typography>
      )}
      {description && (
        <Typography
          variant="caption2"
          component="p"
          color="agentChat.message.agent.foreground.light"
        >
          {description}
        </Typography>
      )}
      <Stack direction="column" gap={1}>
        {options.map((opt) => {
          const optSelected = isOptionSelected(opt.label);

          return (
            <Group
              key={opt.label}
              role="button"
              onClick={() => handleToggle(opt.label)}
              sx={{
                p: 1,
                cursor: isAnswered ? 'default' : 'pointer',
                opacity: isAnswered && !optSelected ? 0.5 : 1,
                border: '1px solid',
                borderColor: optSelected ? palette.primary.dark : palette.input.border.default,
                bgcolor: optSelected
                  ? palette.agentChat.message.agent.backgroundSelected
                  : palette.background.paper,
                borderRadius: 1.5,
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '.agent-chat-multi-choice-block-label': {
                  color: optSelected
                    ? palette.foreground.paper.default
                    : palette.agentChat.message.agent.foreground.default,
                },
                '.agent-chat-multi-choice-block-description': {
                  color: optSelected
                    ? palette.foreground.paper.default
                    : palette.agentChat.message.agent.foreground.light,
                },
                '&:hover': isAnswered
                  ? {}
                  : {
                      bgcolor: palette.agentChat.message.agent.backgroundHover,
                    },
              }}
              data-testid={`multi-choice-option-${opt.label}`}
            >
              <Checkbox checked={optSelected} />
              <Box>
                <Typography variant="body1" className="agent-chat-multi-choice-block-label">
                  {opt.label}
                </Typography>
                {opt.description && (
                  <Typography
                    variant="caption2"
                    component="p"
                    color="agentChat.message.agent.foreground.light"
                    className="agent-chat-multi-choice-block-description"
                  >
                    {opt.description}
                  </Typography>
                )}
              </Box>
            </Group>
          );
        })}
      </Stack>

      {/* Submit button — only shown while pending, disabled until minSelect met */}
      {!isAnswered && (
        <Stack mt={2} gap={1}>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
            fullWidth
            size="small"
            data-testid="multi-choice-submit-btn"
          >
            Submit Selection{selected.size > 1 ? 's' : ''}
          </Button>
          {/* Helper text showing selection constraints */}
          {(minSelect > 1 || maxSelect) && (
            <Typography
              variant="caption2"
              component="p"
              color="agentChat.message.agent.foreground.light"
            >
              {maxSelect
                ? `Select ${minSelect}–${maxSelect} options`
                : `Select at least ${minSelect}`}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};
